import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import classNames from "classnames";
import styles from "./styles.module.scss";
import { Option, Select } from "@components/ui-kit/Select";
import { Input } from "@components/ui-kit/Input";
import Search from "@assets/icons/search";
import { Spin } from "antd";
import { ProductsService } from "@services/amazon/products/products.service";
import { useDashboardStore } from "@pages/Dashboard/store/dashboard.state";

const PAGE_SIZE = 32;

interface BrandAsinSelectProps {
  className?: string;
  leftSideContent?: ReactNode;
}

export const BrandAsinSelect: React.FC<BrandAsinSelectProps> = ({
  className,
  leftSideContent,
}) => {
  const productsService = useRef(new ProductsService()).current;

  const [asinOptions, setAsinOptions] = useState<Option[]>([]);
  const [asinPage, setAsinPage] = useState(1);
  const [asinHasMore, setAsinHasMore] = useState(true);
  const [asinLoading, setAsinLoading] = useState(false);
  const [searchAsin, setSearchAsin] = useState("");
  const [wasSearching, setWasSearching] = useState(false);

  const [brandOptions, setBrandOptions] = useState<Option[]>([]);
  const [brandPage, setBrandPage] = useState(1);
  const [brandHasMore, setBrandHasMore] = useState(true);
  const [brandLoading, setBrandLoading] = useState(false);

  const {
    isAsinSelect,
    asinSelected,
    brandSelected,
    setAsinBrandSelect,
    setBrandSelected,
    setAsinSelected,
    setBrandName,
    setAsinName,
  } = useDashboardStore();

  const loadAsinPage = useCallback(
    async (page: number) => {
      if (asinLoading) return;
      setAsinLoading(true);

      const offset = (page - 1) * PAGE_SIZE;
      const prods = await productsService.getOwnProducts({
        limit: PAGE_SIZE,
        offset,
      });

      if (!prods.length) {
        setAsinHasMore(false);
        setAsinLoading(false);
        return;
      }

      const opts: Option[] = prods.map((p) => ({
        id: p.asin,
        text: p.item_name,
        img: p.image_link,
        asin: p.asin,
      }));

      setAsinOptions((prev) => (page === 1 ? opts : [...prev, ...opts]));
      setAsinName(prods.map((p) => p.asin));
      setAsinLoading(false);
    },
    [asinLoading, productsService, setAsinName],
  );

  const loadBrandPage = useCallback(
    async (page: number) => {
      if (brandLoading) return;
      setBrandLoading(true);

      const offset = (page - 1) * PAGE_SIZE;
      const brands = await productsService.getOwnProductsBrand({
        limit: PAGE_SIZE,
        offset,
      });

      if (!brands.length) {
        setBrandHasMore(false);
        setBrandLoading(false);
        return;
      }

      const opts: Option[] = brands.map((b) => ({
        id: b.name,
        text: b.asins.join(", "),
      }));

      setBrandOptions((prev) => (page === 1 ? opts : [...prev, ...opts]));
      setBrandName(brands.map((b) => b.name));
      setBrandLoading(false);
    },
    [brandLoading, productsService, setBrandName],
  );

  useEffect(() => {
    loadAsinPage(1);
    loadBrandPage(1);
  }, []);

  useEffect(() => {
    const term = searchAsin.trim();

    if (!term) {
      if (!wasSearching) return;
      setWasSearching(false);
      setAsinPage(1);
      setAsinHasMore(true);
      loadAsinPage(1);
      return;
    }

    (async () => {
      setWasSearching(true);
      setAsinLoading(true);

      const res = await productsService.searchOwnProducts({
        limit: PAGE_SIZE,
        asin: term,
      });

      setAsinOptions(
        res.map((p) => ({
          id: p.asin,
          text: p.item_name,
          img: p.image_link,
          asin: p.asin,
        })),
      );
      setAsinHasMore(false);
      setAsinLoading(false);
    })();
  }, [searchAsin, wasSearching, loadAsinPage, productsService]);

  const handleOpen = () => {
    if (isAsinSelect) {
      setAsinPage(1);
      setAsinHasMore(true);
      loadAsinPage(1);
    } else {
      setBrandPage(1);
      setBrandHasMore(true);
      loadBrandPage(1);
    }
  };

  const loadMore = () => {
    if (isAsinSelect) {
      if (asinLoading || !asinHasMore) return;
      const next = asinPage + 1;
      setAsinPage(next);
      loadAsinPage(next);
    } else {
      if (brandLoading || !brandHasMore) return;
      const next = brandPage + 1;
      setBrandPage(next);
      loadBrandPage(next);
    }
  };

  const handleChange = (vals: string[]) => {
    isAsinSelect ? setAsinSelected(vals) : setBrandSelected(vals);
  };
  const handleReset = () => {
    isAsinSelect ? setAsinSelected([]) : setBrandSelected([]);
  };

  const options = isAsinSelect ? asinOptions : brandOptions;
  const selected = isAsinSelect ? asinSelected : brandSelected;
  const hasMore = isAsinSelect ? asinHasMore : brandHasMore;
  const isLoading = isAsinSelect ? asinLoading : brandLoading;

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.switch__container}>
        <button
          className={classNames(styles.switch, {
            [styles.active]: !isAsinSelect,
          })}
          onClick={() => setAsinBrandSelect(false)}
        >
          Brand
        </button>
        <button
          className={classNames(styles.switch, {
            [styles.active]: isAsinSelect,
          })}
          onClick={() => setAsinBrandSelect(true)}
        >
          ASIN
        </button>
      </div>

      <Select
        value={selected}
        options={options}
        onChange={handleChange}
        placeholder={isAsinSelect ? "Select ASIN" : "Select Brand"}
        className={styles.select}
        isMultiSelect
        isOptionsOutside
        onOpen={handleOpen}
        onLoadMore={loadMore}
        hasMore={hasMore}
        leftSideContent={leftSideContent}
      >
        <div className={styles.search}>
          {isAsinSelect && (
            <Input
              placeholder="Search ASIN"
              value={searchAsin}
              icon={<Search />}
              onChange={setSearchAsin}
            />
          )}
          <button className={styles.reset} onClick={handleReset}>
            Reset
          </button>
        </div>
        {isLoading && (
          <div className={styles.loader}>
            <Spin size="small" />
          </div>
        )}
      </Select>
    </div>
  );
};
